import { IonContent, IonHeader, IonPage, IonToolbar, useIonRouter } from "@ionic/react";
import { memo, ReactNode, useCallback } from "react";

import { HeaderSubtitle } from "@/components/header/subtitle";
import { HeaderTitle } from "@/components/header/title";
import { ChevronDownIcon } from "@/components/icons/chevron-down-icon";
import IconButton from "@/components/ui/buttons/IconButton";
import { useSafeAreaStyles } from "@/hooks/utils/use-safe-area-styles";
import { cn } from "@/utils/cn";
import { modalSlideDownTransition } from "@/utils/transitions/modal-slide-up-transition";

interface BottomSheetPageContainerProps {
  children: ReactNode;
  className?: string;
  showToolbar?: boolean;
  bgColor?: string;
  headerBgColor?: string;
  contentBgColor?: string;
  onBack?: () => void;
  title?: string;
  subtitle?: string;
  actionButton?: ReactNode;
  backIcon?: ReactNode;
  backButtonAriaLabel?: string;
}

/**
 * Bottom sheet style page container with slide-down animation on back
 * Uses chevron down icon instead of back arrow for modal-style navigation
 */
export const BottomSheetPageContainer = memo(function BottomSheetPageContainer({
  children,
  className,
  showToolbar = true,
  bgColor = "white",
  headerBgColor = "white",
  contentBgColor = "white",
  onBack,
  title,
  subtitle,
  actionButton,
  backIcon,
  backButtonAriaLabel = "Close",
}: BottomSheetPageContainerProps) {
  const navigation = useIonRouter();
  const safeAreaStyles = useSafeAreaStyles();

  const handleBack = useCallback(() => {
    if (onBack) {
      onBack();
    } else {
      navigation.goBack(modalSlideDownTransition);
    }
  }, [navigation, onBack]);

  return (
    <IonPage color={bgColor ?? "white"} style={{ ...safeAreaStyles }}>
      {showToolbar && (
        <IonHeader color={headerBgColor}>
          <IonToolbar className="safe-area-top mx-auto max-w-[450px]" style={{ ...safeAreaStyles }}>
            <div className="flex w-full items-center bg-white px-6 pt-2">
              <IconButton
                aria-label={backButtonAriaLabel}
                className="shrink-0"
                onClick={handleBack}
              >
                {backIcon ?? <ChevronDownIcon />}
              </IconButton>

              <div className="flex flex-1 flex-col items-center justify-center gap-2">
                {title && <HeaderTitle title={title} titleFontSize="title1" />}
                {subtitle && <HeaderSubtitle subtitle={subtitle} subtitleFontSize="bodySmall" />}
              </div>

              {actionButton ? actionButton : <div slot="end" className="h-[50px] w-[50px]" />}
            </div>
          </IonToolbar>
        </IonHeader>
      )}
      <IonContent fullscreen color={contentBgColor}>
        <div
          className={cn(
            "mx-auto flex h-full w-full max-w-[450px] flex-col items-center justify-start gap-4 overflow-hidden",
            className,
          )}
        >
          {children}
        </div>
      </IonContent>
    </IonPage>
  );
});
